/**
 * Mentions Hooks
 * React Query hooks for mention management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mentionsService } from '@/services/mentions.service';
import type { MentionFilters } from '@/types/api.types';

/**
 * Fetch user's mentions with filters
 */
export const useMentions = (filters?: MentionFilters) => {
  return useQuery({
    queryKey: ['mentions', filters],
    queryFn: () => mentionsService.getMentions(filters),
    staleTime: 30000, // 30 seconds
  });
};

/**
 * Fetch unread mention counts
 */
export const useUnreadMentionCounts = () => {
  return useQuery({
    queryKey: ['mentions', 'unread-counts'],
    queryFn: () => mentionsService.getUnreadCounts(),
    refetchInterval: 30000, // Refresh every 30s
  });
};

/**
 * Get mentions for specific channel
 */
export const useChannelMentions = (channelId: string | null, isRead?: boolean) => {
  return useQuery({
    queryKey: ['mentions', 'channel', channelId, isRead],
    queryFn: () =>
      channelId
        ? mentionsService.getMentions({ channelId, isRead, limit: 100 })
        : Promise.resolve({ mentions: [], total: 0 }),
    enabled: !!channelId,
  });
};

/**
 * Get mentions for specific DM thread
 */
export const useDMMentions = (dmThreadId: string | null, isRead?: boolean) => {
  return useQuery({
    queryKey: ['mentions', 'dm', dmThreadId, isRead],
    queryFn: () =>
      dmThreadId
        ? mentionsService.getMentions({ dmThreadId, isRead, limit: 100 })
        : Promise.resolve({ mentions: [], total: 0 }),
    enabled: !!dmThreadId,
  });
};

/**
 * Mark mention as read mutation
 */
export const useMarkMentionRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mentionId: string) => mentionsService.markAsRead(mentionId),
    onSuccess: () => {
      // Invalidate all mention queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['mentions'] });
    },
  });
};

/**
 * Mark all mentions in conversation as read mutation
 */
export const useMarkConversationMentionsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ channelId, dmThreadId }: { channelId?: string; dmThreadId?: string }) =>
      mentionsService.markConversationAsRead(channelId, dmThreadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mentions'] });
    },
  });
};

/**
 * Get unread mention count for specific channel
 */
export const useChannelMentionCount = (channelId: string | null) => {
  const { data: counts } = useUnreadMentionCounts();
  return {
    data: channelId && counts?.channels ? counts.channels[channelId] || 0 : 0,
  };
};

/**
 * Get unread mention count for specific DM thread
 */
export const useDMMentionCount = (dmThreadId: string | null) => {
  const { data: counts } = useUnreadMentionCounts();
  return {
    data: dmThreadId && counts?.dms ? counts.dms[dmThreadId] || 0 : 0,
  };
};
